// An off-screen copy of the flight's video that can be pointed at a time and
// asked for the whole picture, without touching the video the reviewer is
// watching. A POI's "Save frame" in the viewer grabs the POI's moment with it,
// and the web app's Saved Frames page uses it to save frames for POIs in bulk.
// One home, so the two cannot drift.
import { isWorkerUrl, topLevelIndex } from "./viewer.quality.js";

/** Thrown when no capture can ever work with this grabber (the video will not
 *  load, the canvas is tainted, the browser cannot draw video); any other
 *  error from grab() fails only that one picture. */
export class CaptureFatalError extends Error {}

/**
 * Present on screen for the decoder's sake, invisible to the reviewer.
 *
 * Inside the viewport on purpose: Safari renders nothing for a video that
 * sits outside it (or at opacity 0), so its play() never starts and
 * drawImage paints nothing. Two pixels at 2 % in the corner is on screen
 * as far as WebKit is concerned and invisible to a person; it stays on top
 * rather than under the page so nothing can be said to cover it. Never
 * display:none, for the same reason.
 */
const HIDDEN_STYLE =
  "position:fixed;left:0;bottom:0;width:2px;height:2px;opacity:0.02;" +
  "pointer-events:none;z-index:2147483647;";

/**
 * A cancellable wait for `loadedmetadata`. `cancel()` clears the timer and
 * removes both listeners without settling the promise, so a caller that no
 * longer needs the wait (the load already failed some other way) can walk
 * away without leaving a live 30 s timer or an unhandled rejection behind.
 */
function waitForMetadata(video, timeoutMs) {
  let onLoaded = () => undefined;
  let onError = () => undefined;
  let timer;
  const promise = new Promise((resolve, reject) => {
    const done = (err) => {
      clearTimeout(timer);
      video.removeEventListener("loadedmetadata", onLoaded);
      video.removeEventListener("error", onError);
      if (err === undefined) resolve(); else reject(err);
    };
    onLoaded = () => done();
    onError = () => done(new CaptureFatalError("The flight's video didn't load."));
    timer = setTimeout(
      () => done(new CaptureFatalError("The flight's video took too long to load.")),
      timeoutMs,
    );
    video.addEventListener("loadedmetadata", onLoaded);
    video.addEventListener("error", onError);
  });
  const cancel = () => {
    // Clearing the timer and detaching both listeners means `done` above
    // can never run again, so `promise` is left forever unsettled — never
    // resolved, never rejected, and (because nothing is awaiting it any
    // more once the caller reaches here) never an unhandled rejection.
    clearTimeout(timer);
    video.removeEventListener("loadedmetadata", onLoaded);
    video.removeEventListener("error", onError);
  };
  return { promise, cancel };
}

/**
 * A cancellable seek. `cancel()` clears the timer and detaches the `seeked`
 * listener without settling the promise — used when a fatal hls error wins
 * the race against this seek, so the seek's own 20 s timer doesn't outlive
 * the grab it belonged to.
 */
function seek(video, t, timeoutMs) {
  let onSeeked = () => undefined;
  let timer;
  const promise = new Promise((resolve, reject) => {
    onSeeked = () => { clearTimeout(timer); resolve(); };
    timer = setTimeout(() => {
      video.removeEventListener("seeked", onSeeked);
      reject(new Error("seek timed out"));
    }, timeoutMs);
    video.addEventListener("seeked", onSeeked, { once: true });
    // A same-value assignment fires no `seeked` in some browsers.
    video.currentTime = Math.abs(video.currentTime - t) < 0.0005 ? t + 0.001 : t;
  });
  const cancel = () => {
    clearTimeout(timer);
    video.removeEventListener("seeked", onSeeked);
  };
  return { promise, cancel };
}

/** `seeked` can land a beat before the new picture is on the element. */
function settle(video, ms) {
  if (ms <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    video.requestVideoFrameCallback?.(() => { clearTimeout(timer); resolve(); });
  });
}

/**
 * One muted, inline play and pause, right after the metadata arrives.
 *
 * WebKit paints nothing from a video element that has never played: the
 * seek completes, `drawImage` leaves the canvas transparent and every POI
 * of a Safari run fails with "no picture". Starting playback once sets its
 * decoder up; muted + playsInline is what makes play() allowed with no
 * gesture. A refusal (autoplay policy) or a play that never starts (no
 * data within `timeoutMs`) is not an error: the grab path still has its
 * own blank-draw retries.
 */
async function warmUp(video, timeoutMs) {
  if (typeof video.play !== "function") return;
  let timer;
  const cap = new Promise((resolve) => { timer = setTimeout(resolve, timeoutMs); });
  try {
    await Promise.race([Promise.resolve(video.play()).catch(() => undefined), cap]);
  } finally {
    clearTimeout(timer);
    video.pause?.();
  }
}

/**
 * Push one frame through the display path after a seek that painted
 * nothing: play until a frame is presented (or `maxMs`), then pause. Moves
 * the time on by a frame or two at most, which a freeze frame at a POI's
 * moment does not notice.
 */
async function nudge(video, maxMs) {
  if (typeof video.play !== "function") return;
  try {
    const started = Promise.resolve(video.play()).catch(() => undefined);
    await Promise.race([started.then(() => settle(video, maxMs)), settle(video, maxMs)]);
  } finally {
    video.pause?.();
  }
}

/**
 * An off-screen player for the flight's video that can be pointed at a time
 * and asked for the picture. HLS flights are pinned to the tallest level, the
 * one a hand-pressed "Save frame" captures from.
 *
 * @param {object} opts
 * @param {string} [opts.videoUrl]     MP4 of a progressive flight.
 * @param {string} [opts.manifestUrl]  Master manifest of an HLS flight; wins when both are set.
 * @param {string} [opts.authToken]    Bearer token, sent to Worker manifest URLs only.
 * @param {string} [opts.workerOrigin]
 * @param {any}    [opts.Hls]          hls.js constructor; defaults to globalThis.Hls.
 * @param {Document} [opts.doc]
 * @param {number} [opts.loadTimeoutMs]
 * @param {number} [opts.seekTimeoutMs]
 * @param {number} [opts.frameSettleMs]
 * @param {number} [opts.warmUpTimeoutMs] Cap on the one play/pause that wakes WebKit's decoder.
 * @param {number} [opts.blankRetries]    More draws to try when the first came out blank…
 * @param {number} [opts.blankRetryMs]    …and the gap between them.
 * @returns {Promise<{ readonly duration: number,
 *   grab(videoTimeS: number): Promise<HTMLCanvasElement>, dispose(): void }>}
 */
export async function createVideoGrabber(opts) {
  const doc = opts.doc ?? document;
  const loadTimeoutMs = opts.loadTimeoutMs ?? 30_000;
  const seekTimeoutMs = opts.seekTimeoutMs ?? 20_000;
  const frameSettleMs = opts.frameSettleMs ?? 300;
  const warmUpTimeoutMs = opts.warmUpTimeoutMs ?? 8_000;
  const blankRetries = opts.blankRetries ?? 3;
  const blankRetryMs = opts.blankRetryMs ?? 250;

  const video = doc.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "metadata";
  // Before the source, or the canvas is tainted and nothing can be read back.
  video.crossOrigin = "anonymous";
  video.style.cssText = HIDDEN_STYLE;
  doc.body.appendChild(video);

  let hls = null;
  // Set as soon as the metadata wait exists (the first line of the try
  // block below) so every throw path — reached only through the catch,
  // which always calls dispose() — cancels the same wait: no live 30 s
  // timer or dangling listener survives a failed load.
  let cancelMetadataWait = null;
  // Sticky once hls reports a fatal error: this stream is dead and no
  // future grab() can ever succeed, so grab() checks this first and skips
  // seeking entirely (a bulk capture over many POIs must fail fast, not
  // burn a full seek timeout per remaining POI). `onFatalDuringGrab`, when
  // set, is the one grab currently waiting on a seek — a fatal error that
  // lands mid-seek rejects it immediately instead of after seekTimeoutMs.
  let fatalErr = null;
  let onFatalDuringGrab = null;
  const dispose = () => {
    cancelMetadataWait?.();
    hls?.destroy();
    hls = null;
    video.removeAttribute("src");
    video.load();
    video.remove();
  };

  try {
    const { promise: loaded, cancel } = waitForMetadata(video, loadTimeoutMs);
    cancelMetadataWait = cancel;
    if (opts.manifestUrl !== undefined) {
      const Hls = opts.Hls ?? globalThis.Hls;
      if (Hls === undefined || !Hls.isSupported()) {
        throw new CaptureFatalError("This browser can't capture frames from this flight.");
      }
      const player = new Hls({
        // About one 4 s segment per seek; the 30 s default would pull ~250 MB.
        // hls.js's real buffer target is
        // min(max(8*maxBufferSize/bitrate, maxBufferLength), maxMaxBufferLength),
        // so maxBufferLength alone barely helps at high bitrate — at ~70 Mbps
        // the default 60 MB maxBufferSize alone works out to ~2 s. Zeroing
        // maxBufferSize makes the target max(0, 1) = 1 s, so it actually governs.
        maxBufferLength: 1,
        maxBufferSize: 0,
        maxMaxBufferLength: 2,
        backBufferLength: 0,
        capLevelToPlayerSize: false,
        // The token goes to the Worker's manifests only. Presigned R2 segment
        // URLs carry their own auth and must not get the header.
        xhrSetup: (xhr, url) => {
          if (opts.authToken !== undefined && isWorkerUrl(url, opts.workerOrigin)) {
            xhr.setRequestHeader("Authorization", `Bearer ${opts.authToken}`);
          }
        },
      });
      hls = player;
      const fatal = new Promise((_, reject) => {
        player.on(Hls.Events.ERROR, (_evt, data) => {
          if (data?.fatal === true) {
            const err = new CaptureFatalError(
              `The flight's video didn't load (${data.details ?? "hls"}).`,
            );
            // A fatal error is terminal for the whole stream, not just the
            // initial load: mark it sticky and wake whichever grab (if any)
            // is currently mid-seek, in addition to the initial-load race
            // below.
            fatalErr = err;
            onFatalDuringGrab?.(err);
            reject(err);
          }
        });
      });
      player.on(Hls.Events.MANIFEST_PARSED, () => {
        const top = topLevelIndex(player.levels);
        if (top >= 0) {
          player.startLevel = top;
          player.currentLevel = top; // immediate switch: flushes anything lower
        }
      });
      player.loadSource(opts.manifestUrl);
      player.attachMedia(video);
      // `loaded` never needs a catch here: on every reachable path it
      // either resolves (this race is won by `loaded`) or is left pending
      // by dispose()'s cancelMetadataWait() call on the failure path below
      // (waitForMetadata's cancel() detaches its listeners and clears its
      // timer without ever settling the promise) — so it can never reject
      // unhandled. `fatal` has no such cancellation path: a genuine later
      // hls error (after this call has already returned, with `loaded`
      // long since won) has nothing analogous to clearTimeout to silence
      // it, so it keeps a permanent no-op catch.
      fatal.catch(() => undefined);
      await Promise.race([loaded, fatal]);
    } else {
      if (opts.videoUrl === undefined || opts.videoUrl === "") {
        throw new CaptureFatalError("This flight has no video to capture from.");
      }
      video.src = opts.videoUrl;
      await loaded;
    }
    await warmUp(video, warmUpTimeoutMs);
  } catch (err) {
    dispose();
    throw err;
  }

  return {
    get duration() { return video.duration; },
    async grab(videoTimeS) {
      if (fatalErr !== null) throw fatalErr;
      const { promise: seeked, cancel: cancelSeek } = seek(video, videoTimeS, seekTimeoutMs);
      const duringGrab = new Promise((_, reject) => { onFatalDuringGrab = reject; });
      try {
        await Promise.race([seeked, duringGrab]);
      } finally {
        // Whichever of the two lost the race is torn down here: cancelSeek()
        // clears the seek's own timer/listener so it can't fire minutes
        // later, and un-registering the listener leaves `duringGrab`
        // permanently pending (never rejected) rather than an unhandled
        // rejection — the same pattern waitForMetadata's cancel() uses.
        onFatalDuringGrab = null;
        cancelSeek();
      }
      await settle(video, frameSettleMs);
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (w === 0 || h === 0) throw new Error(`no picture at that time (${w}x${h})`);
      const canvas = doc.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (ctx === null) throw new CaptureFatalError("This browser can't draw video frames.");
      // A paused or backgrounded-tab video is a known risk (Safari
      // especially) for drawImage painting nothing: the canvas stays
      // transparent and the JPEG would come out a confident-looking black
      // frame. A real video frame is always opaque, so a transparent probe
      // pixel means the picture was not there yet; WebKit decodes after
      // `seeked`, so give it a few more goes before calling this grab
      // failed — not a fatal, just this POI.
      for (let attempt = 0; ; attempt += 1) {
        ctx.drawImage(video, 0, 0, w, h);
        let probe;
        try {
          probe = ctx.getImageData(0, 0, 1, 1);
        } catch {
          // The caller can't release this canvas: grab() never returns it
          // on this path, so the freshly allocated full-size backing memory
          // would otherwise leak.
          canvas.width = 0;
          canvas.height = 0;
          throw new CaptureFatalError(
            "The video can't be read back (cross-origin without CORS), so frames can't be saved.",
          );
        }
        if (probe.data[3] !== 0) return canvas;
        if (attempt >= blankRetries) break;
        // First a moment of playback to make the browser present a frame
        // (WebKit decodes for the display, not for the seek), then a wait.
        if (attempt === 0) await nudge(video, Math.max(blankRetryMs, 100));
        await settle(video, blankRetryMs);
      }
      // Same leak risk as above: release the canvas before throwing.
      canvas.width = 0;
      canvas.height = 0;
      throw new Error(`no picture at that time (blank after ${blankRetries + 1} draws)`);
    },
    dispose,
  };
}

/**
 * A grabber built on the first grab() and reused after, for the viewer's POI
 * "Save frame": no hidden video until someone asks for a frame. Two presses
 * at once share one build. A build that fails, or a grabber whose stream has
 * died (a CaptureFatalError from grab), is dropped so the next press starts
 * afresh; an ordinary failed grab keeps it. dispose() tears down whatever
 * was built, including one still loading when the viewer closes.
 *
 * @param {() => Promise<{grab(t: number): Promise<HTMLCanvasElement>, dispose(): void}>} make
 */
export function lazyGrabber(make) {
  let building = null; // the build in flight or done, shared by every press
  let built = null; // the grabber that build produced, for a synchronous dispose
  let disposed = false;
  return {
    async grab(videoTimeS) {
      if (disposed) throw new Error("the viewer has closed");
      if (building === null) {
        // The executor runs make() now; a synchronous throw becomes a rejection.
        const attempt = new Promise((resolve) => resolve(make()));
        building = attempt;
        attempt.then(
          (g) => { if (building === attempt) built = g; },
          () => { if (building === attempt) building = null; },
        );
      }
      const mine = building;
      const g = await mine;
      if (disposed) {
        g.dispose();
        throw new Error("the viewer has closed");
      }
      try {
        return await g.grab(videoTimeS);
      } catch (err) {
        if (err instanceof CaptureFatalError && building === mine) {
          building = null;
          built = null;
          g.dispose();
        }
        throw err;
      }
    },
    dispose() {
      disposed = true;
      built?.dispose();
      built = null;
      building = null;
    },
  };
}
